import { setCurrPlanCode, setCurrUTourUrl } from 'features/siteMapSlice';
import { PARTNER_TOKENS } from 'lib/constants';
import gaFunction from 'lib/ga';
import { useUserDataMutation } from 'lib/hooks/mutations';
import { useAppDispatch } from 'lib/hooks/redux';
import {
  IElevation,
  IFloorPlan,
  ILotInfo,
  IOrientation,
  IPhase,
  ISiteMapDisplayOption,
  IUserData,
} from 'lib/models/siteMap.interface';
import InlineSVG from 'react-inlinesvg/esm';
import styled from 'styled-components';
import utourIcon from 'lib/assets/utour.svg';
import { Tooltip } from 'antd';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';

interface Props {
  baseUrl: string;
  orientation: IOrientation;
  plan: IFloorPlan;
  elevation: IElevation;
  phase: IPhase;
  lot: ILotInfo;
  lotTitle: string;
  displayOption: ISiteMapDisplayOption;
  isolatePlan: boolean;
  userData?: IUserData;
}

const LotInfoCard = ({
  baseUrl,
  orientation,
  plan,
  elevation,
  phase,
  lot,
  lotTitle,
  displayOption,
  isolatePlan,
  userData,
}: Props) => {
  // Redux Hooks
  const dispatch = useAppDispatch();

  const userDataMutation = useUserDataMutation();

  const { isMobile } = useWindowDimensions();

  // Constants
  const URLParams = new URLSearchParams(window.location.search);
  const partnerToken = URLParams.get('partnerToken');

  // Functions
  const handleShowFloorPlan = () => {
    dispatch(setCurrPlanCode(plan.code));
    gaFunction.spSelectPlanFromLot(
      lotTitle,
      orientation.code === 'REV' ? plan.nameREV : plan.name,
      elevation.style
    );
  };

  // const handleIsolatePlan = () => {
  //   dispatch(setIsolatePlan(!isolatePlan));
  // };

  // Partner Integration
  const selectLot = async () => {
    if (partnerToken && PARTNER_TOKENS.includes(partnerToken) && userData) {
      const jsonUserData = {
        lotInfo: {
          assetUrl: `${baseUrl}/${elevation.folder}/${elevation.fileName}`,
          planName: orientation.code === 'REV' ? plan.nameREV : plan.name,
          planCode: plan.code,
          elevationName: elevation.style,
          phase: phase.name,
          orientation: orientation.label,
          sqft: lot.squareFeet,
          price: lot.price,
          address: lot.address,
          description: lot.description,
          description2: lot.description2,
          description3: lot.description3,
        },
      };

      userDataMutation.mutate({
        ...userData,
        partnerToken,
        jsonUserData,
        app: 'sp',
      });
    }
  };

  return (
    <StyledCard
      isReversed={Boolean(Number(orientation.code === 'REV') ^ Number(plan.flipPlanGraphic))}
      isMobile={isMobile}
    >
      <div className="img-wrapper">
        <img
          src={`${baseUrl}/${elevation.folder}/${elevation.fileName}`}
          alt={elevation.fileName}
          data-cy="lot-modal-image"
        />
      </div>
      <div className="card-body" data-cy="lot-card-body">
        {!displayOption.hidePlanName && (
          <h4>{orientation.code === 'REV' ? plan.nameREV : plan.name}</h4>
        )}
        {!displayOption.hideElevationName && <h4>{elevation.style}</h4>}
        {!displayOption.hidePhase && <h4>{phase.name}</h4>}
        {!displayOption.hideOrientation && <h4>{orientation.label}</h4>}
        {lot.squareFeet && <h4>{lot.squareFeet.toLocaleString()} Sq. Ft.</h4>}
        {lot.price && <h4>${lot.price.toLocaleString()}</h4>}
        {lot.address && <h4>Address: {lot.address}</h4>}
        {lot.description && <h4>{lot.description}</h4>}
        {lot.description2 && <h4>{lot.description2}</h4>}
        {lot.description3 && <h4>{lot.description3}</h4>}
      </div>
      <StyledButtonWrapper>
        {!partnerToken && (
          <StyledButton onClick={handleShowFloorPlan} data-cy="show-floor-plan-button">
            SHOW FLOOR PLAN
          </StyledButton>
        )}
        {/* {communityType !== 'SFM' && (
          <StyledButton onClick={handleIsolatePlan} active={isolatePlan}>
            ISOLATE PLAN
          </StyledButton>
        )} */}
        {lot.utourUrl && (
          <Tooltip title="Schedule a tour of this lot.">
            <StyledUTourIcon
              src={utourIcon}
              onClick={() => dispatch(setCurrUTourUrl(lot.utourUrl!))}
              data-cy="u-tour-button"
            />
          </Tooltip>
        )}
      </StyledButtonWrapper>
      {partnerToken && PARTNER_TOKENS.includes(partnerToken) && (
        <StyledButton onClick={selectLot}>SELECT LOT/PLAN</StyledButton>
      )}
    </StyledCard>
  );
};

export default LotInfoCard;

const StyledCard = styled.div<{ isReversed: boolean; isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 30px;
  img {
    width: fit-content;
    max-width: 100%;
    max-height: 100%;
    border-radius: calc(0.25rem - 1px);
    ${({ isReversed }) =>
      isReversed &&
      `
        transform: scaleX(-1);
      `}
  }

  .img-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${({ isMobile }) => (isMobile ? '25vh' : '30vh')};
  }

  .card-body {
    padding: 1vw;
    flex: none;
    text-align: center;
  }
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 1vw;
`;

const StyledButton = styled.button<{ active?: boolean }>`
  background-color: ${({ active, theme }) => (active ? `${theme.colors.primaryColor}` : '')};
  color: ${({ active, theme }) => (active ? `${theme.colors.textColor}` : '')};
  transition: all 0.3s ease 0s;
  border: none;
  font-weight: bold;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4vh;
`;

const StyledUTourIcon = styled(InlineSVG)`
  height: 5vh;
  width: auto;
  cursor: pointer;
`;
